import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2, Search, User, Users } from "lucide-react";
import { GroupOfPeopleIcon } from "../../../../shared/ui/icons/SubjectKindIcon";

import { Modal } from "../../../../shared/ui/modal";
import { useTherapist } from "../../../../context/useTherapist";
import { offeredCaseload, useCourseLabels, useSessionPreferences } from "../../../center";
import { type Patient, groupLabel, type GroupPatient } from "../../../patient";
import { loadMeditationRecipientCaseload } from "../../application/meditationRecipients";
import type { CouplePatient } from "../../../patient";
import {
    useMeditationRecipients,
    useShareMeditation,
    useUnshareMeditation,
} from "../../application/useMeditations";

type Props = {
    meditationId: string;
    title: string;
    isOpen: boolean;
    onClose: () => void;
};

type RecipientKind = "patient" | "couple" | "group";

/** Somebody the meditation can go to: one patient, or everybody in a couple or a group. */
interface Recipient {
    key: string;
    kind: RecipientKind;
    name: string;
    memberIds: string[];
}

const ICONS = { patient: User, couple: Users, group: GroupOfPeopleIcon } as const;

/**
 * Who gets to keep this meditation.
 *
 * A guided meditation is practised, not attended: the therapist reads it once
 * in the session, and what changes anything is the patient putting it on again
 * on Tuesday night. This is where that is arranged — the meditation appears in
 * the patient's own portal, to read or to play, and nothing else. They cannot
 * edit it, and there is no copy: what the therapist changes tonight is what the
 * patient hears tomorrow.
 *
 * One row per patient with the state on it, rather than a two-list
 * "available / shared" arrangement: a therapist looking for somebody types
 * their name, and the answer to "does she have it?" should be on the row they
 * find rather than in which column it landed in.
 *
 * A couple or a group is a row too. Giving it to them gives it to each member,
 * in their own portal — there is no shared portal to put it in — and the row
 * says how many of them have it, since a member can also be given it, or have
 * it taken back, on their own.
 */
export default function MeditationShareSheet({
    meditationId,
    title,
    isOpen,
    onClose,
}: Props) {
    const { t } = useTranslation();
    const { therapist } = useTherapist();
    // A training centre's student is named with the course they are on.
    const { labelOf } = useCourseLabels();

    const [recipientsList, setRecipientsList] = useState<Recipient[]>([]);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [query, setQuery] = useState("");

    const recipients = useMeditationRecipients(isOpen ? meditationId : undefined);
    // Couples and groups only where the centre runs them.
    const { modes } = useSessionPreferences();
    const share = useShareMeditation();
    const unshare = useUnshareMeditation();

    useEffect(() => {
        if (!isOpen || !therapist?.id) return;
        setLoading(true);
        setFailed(false);
        loadMeditationRecipientCaseload(therapist.id)
            .then((caseload) => offeredCaseload(caseload, modes))
            .then(({ patients, couples, groups }) =>
                setRecipientsList([
                    ...(patients ?? []).map((patient: Patient) => ({
                        key: `patient:${patient.id}`,
                        kind: "patient" as const,
                        name: labelOf(patient),
                        memberIds: [patient.id],
                    })),
                    ...(couples ?? []).map((couple: CouplePatient) => ({
                        key: `couple:${couple.id}`,
                        kind: "couple" as const,
                        name: couple.display_name,
                        memberIds: [couple.patient_1_id, couple.patient_2_id],
                    })),
                    ...(groups ?? []).map((group: GroupPatient) => ({
                        key: `group:${group.id}`,
                        kind: "group" as const,
                        name: groupLabel(group),
                        memberIds: group.members.map((member) => member.id),
                    })),
                ])
            )
            .catch(() => setFailed(true))
            .finally(() => setLoading(false));
    }, [isOpen, therapist?.id, labelOf, modes]);

    useEffect(() => {
        if (!isOpen) setQuery("");
    }, [isOpen]);

    const sharedWith = useMemo(
        () => new Set((recipients.data ?? []).map((row) => row.patient_id)),
        [recipients.data]
    );

    const matches = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return recipientsList;
        return recipientsList.filter((recipient) =>
            recipient.name.toLowerCase().includes(needle)
        );
    }, [recipientsList, query]);

    /** Which row is mid-flight, so only that one shows a spinner. */
    const [pending, setPending] = useState<string | null>(null);

    const sharedCount = (recipient: Recipient) =>
        recipient.memberIds.filter((id) => sharedWith.has(id)).length;

    const toggle = async (recipient: Recipient) => {
        setPending(recipient.key);
        const everybody = sharedCount(recipient) === recipient.memberIds.length;
        try {
            // Everybody has it: take it back from all. Otherwise give it to
            // whoever does not have it yet, and leave the rest as they are.
            for (const patientId of recipient.memberIds) {
                if (everybody) {
                    await unshare.mutateAsync({ id: meditationId, patientId });
                } else if (!sharedWith.has(patientId)) {
                    await share.mutateAsync({ id: meditationId, patientId });
                }
            }
        } catch {
            setFailed(true);
        } finally {
            setPending(null);
        }
    };

    const stateLabel = (recipient: Recipient) => {
        const count = sharedCount(recipient);
        const total = recipient.memberIds.length;
        if (count === total) return t("meditation.share.shared");
        if (count > 0) return t("meditation.share.partial", { shared: count, total });
        return t("meditation.share.give");
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="mx-4 max-w-lg rounded-3xl">
            <div className="flex max-h-[80vh] flex-col p-5">
                <h2 className="mb-1 text-lg font-bold text-content-primary">
                    {t("meditation.share.title")}
                </h2>
                <p className="mb-4 text-sm text-content-tertiary">
                    {t("meditation.share.subtitle", { title })}
                </p>

                <div className="relative mb-3">
                    <Search
                        size={16}
                        aria-hidden="true"
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-tertiary"
                    />
                    <input
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={t("meditation.share.searchPlaceholder")}
                        aria-label={t("meditation.share.searchPlaceholder")}
                        className="w-full rounded-xl border border-border-subtle bg-surface-sunken py-2.5 pl-9 pr-3 text-sm text-content-primary outline-none focus:border-brand-300 focus:ring-2 focus:ring-brand-100"
                    />
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto">
                    {loading || recipients.isLoading ? (
                        <p className="flex items-center justify-center gap-2 py-8 text-sm text-content-tertiary">
                            <Loader2
                                size={16}
                                className="animate-spin"
                                aria-hidden="true"
                            />
                            {t("common.loading")}
                        </p>
                    ) : matches.length === 0 ? (
                        <p className="py-6 text-center text-sm text-content-tertiary">
                            {t("meditation.share.noPatients")}
                        </p>
                    ) : (
                        <ul className="space-y-1">
                            {matches.map((recipient) => {
                                const Icon = ICONS[recipient.kind];
                                const count = sharedCount(recipient);
                                const shared = count === recipient.memberIds.length;
                                return (
                                    <li key={recipient.key}>
                                        <button
                                            type="button"
                                            onClick={() => void toggle(recipient)}
                                            disabled={pending !== null}
                                            aria-pressed={shared}
                                            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-surface-sunken disabled:opacity-50"
                                        >
                                            <span className="flex size-9 items-center justify-center rounded-full bg-surface-sunken text-content-tertiary">
                                                <Icon size={16} aria-hidden="true" />
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-medium text-content-primary">
                                                    {recipient.name}
                                                </span>
                                                {recipient.kind !== "patient" && (
                                                    <span className="block text-xs text-content-tertiary">
                                                        {t(`common.${recipient.kind}`)}
                                                    </span>
                                                )}
                                            </span>
                                            {pending === recipient.key ? (
                                                <Loader2
                                                    size={16}
                                                    className="animate-spin text-content-tertiary"
                                                    aria-hidden="true"
                                                />
                                            ) : (
                                                <span
                                                    className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                                                        count > 0
                                                            ? "bg-accent-tint text-accent-on-tint"
                                                            : "text-content-tertiary"
                                                    }`}
                                                >
                                                    {shared && (
                                                        <Check
                                                            size={13}
                                                            aria-hidden="true"
                                                        />
                                                    )}
                                                    {stateLabel(recipient)}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {failed && (
                    <p role="alert" className="mt-3 text-sm text-status-critical">
                        {t("meditation.share.failed")}
                    </p>
                )}

                <p className="mt-4 text-xs leading-relaxed text-content-tertiary">
                    {t("meditation.share.readOnlyNotice")}
                </p>
            </div>
        </Modal>
    );
}
